import type { CSSProperties, ReactNode } from 'react';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import { CloseButton } from '../CloseButton';
import { TourCarousel } from './TourCarousel';
import type { TourStep, TourLabels } from './types';
import type { SimpleRect } from './Tour';

const DEFAULT_LABELS: Required<TourLabels> = {
  stepOf: (current, total) => `Paso ${current} de ${total}`,
  prev: '← Anterior',
  next: 'Siguiente →',
  done: '¡Listo! 🚀',
};

/**
 * Parsea un subconjunto mínimo de markdown — solo `**negrita**` — a nodos React.
 * No usa dangerouslySetInnerHTML: construye <strong> seguros, sin permitir HTML arbitrario.
 */
function renderWithBold(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <Fragment key={index}>{part}</Fragment>;
  });
}

interface TourPopoverProps {
  targetRect: SimpleRect | null;
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  isFirst: boolean;
  isLast: boolean;
  labels?: TourLabels;
  onPrev(): void;
  onNext(): void;
  onDone(): void;
  onClose(): void;
}

const POPOVER_OFFSET = 14;

type PopoverPosition = { top: number; left: number };
type ArrowPosition = { side: 'top' | 'right' | 'bottom' | 'left'; offset: number };

/**
 * Calcula la posición del popover bajo el target (placement bottom-center por defecto).
 * Síncrono y determinista — el popover y el cutout se mueven en sincronía.
 */
function computePosition(
  rect: SimpleRect,
  popW: number,
  popH: number
): { popover: PopoverPosition; arrow: ArrowPosition } {
  const targetCenterX = rect.left + rect.width / 2;

  // Por defecto: bottom-center. Si no cabe debajo, se coloca encima.
  const fitsBelow = rect.top + rect.height + POPOVER_OFFSET + popH < window.innerHeight;

  let top: number;
  let arrowSide: ArrowPosition['side'];
  if (fitsBelow) {
    top = rect.top + rect.height + POPOVER_OFFSET;
    arrowSide = 'top';
  } else {
    top = rect.top - popH - POPOVER_OFFSET;
    arrowSide = 'bottom';
  }

  let left = targetCenterX - popW / 2;
  const arrowOffset = popW / 2 - 6;

  // Clamp al viewport para que no se salga
  const margin = 8;
  left = Math.max(margin, Math.min(left, window.innerWidth - popW - margin));
  top = Math.max(margin, Math.min(top, window.innerHeight - popH - margin));

  return { popover: { top, left }, arrow: { side: arrowSide, offset: arrowOffset } };
}

export function TourPopover({
  targetRect,
  step,
  stepIndex,
  totalSteps,
  isFirst,
  isLast,
  labels,
  onPrev,
  onNext,
  onDone,
  onClose,
}: TourPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  const stepOfLabel = labels?.stepOf ?? DEFAULT_LABELS.stepOf;
  const prevLabel = labels?.prev ?? DEFAULT_LABELS.prev;
  const nextLabel = labels?.next ?? DEFAULT_LABELS.next;
  const doneLabel = labels?.done ?? DEFAULT_LABELS.done;

  // Medir el popover tras montar. offsetWidth/offsetHeight (no getBoundingClientRect)
  // porque la animación pop-in arranca con scale(0.4) y falsearía las medidas.
  useLayoutEffect(() => {
    if (popoverRef.current) {
      setSize({ w: popoverRef.current.offsetWidth, h: popoverRef.current.offsetHeight });
    }
  }, []);

  const computed = targetRect && size ? computePosition(targetRect, size.w, size.h) : null;

  const isCentered = !targetRect;
  // Variante ancha: un paso centrado con carrusel necesita más espacio que el popover estándar
  // (la captura se ve mucho mejor de primeras). Solo aplica centrado — anclado a un target debe
  // mantener el ancho estándar para caber junto al elemento resaltado.
  const isWide = isCentered && step.media?.type === 'carousel';
  const popoverStyle: CSSProperties = isCentered
    ? // La tarjeta ancha es alta (carrusel grande): se centra al 50% para dejar margen igual
      // arriba y abajo y no tocar el borde superior. El popover centrado normal queda al 40%.
      { position: 'fixed', top: isWide ? '50%' : '40%', left: '50%', opacity: 1 }
    : computed
      ? {
          position: 'fixed',
          top: `${computed.popover.top}px`,
          left: `${computed.popover.left}px`,
          opacity: 1,
        }
      : { position: 'fixed', top: 0, left: 0, opacity: 0 };

  return (
    <div
      ref={popoverRef}
      style={popoverStyle}
      className={`klonup-tour-popover${isCentered ? ' is-centered' : ''}${isWide ? ' is-wide' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`tour-title-${step.id}`}
    >
      {/* Flecha (oculta en paso centrado) */}
      {targetRect && computed && (
        <div
          className="klonup-tour-arrow"
          style={{
            position: 'absolute',
            left: `${computed.arrow.offset}px`,
            [computed.arrow.side]: '-6px',
          }}
        />
      )}

      {/* Cerrar */}
      <div className="klonup-tour-close">
        <CloseButton onClick={onClose} ariaLabel="Cerrar tutorial" size="sm" />
      </div>

      {/* Progreso */}
      <div className="klonup-tour-progress-text">{stepOfLabel(stepIndex + 1, totalSteps)}</div>

      {/* Título */}
      <h2 id={`tour-title-${step.id}`} className="klonup-tour-title">
        {step.title}
      </h2>

      {/* Descripción (soporta **negrita** en markdown simple) */}
      <p className="klonup-tour-description">{renderWithBold(step.description)}</p>

      {/* Media opcional (p.ej. vídeo de ayuda). Con `src` → vídeo; sin `src` → hueco reservado. */}
      {step.media?.type === 'video' && (
        step.media.src ? (
          <video
            className="klonup-tour-media"
            src={step.media.src}
            poster={step.media.poster}
            controls
            aria-label={step.media.label}
          />
        ) : (
          <div className="klonup-tour-media klonup-tour-media-placeholder" role="img" aria-label={step.media.label}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            {step.media.label && <span>{step.media.label}</span>}
          </div>
        )
      )}

      {/* Carrusel de imágenes (auto-avanza en bucle con puntitos). Útil para pasos recortados
          a la zona relevante — más claro que un vídeo que se ve pequeño en el popover. */}
      {step.media?.type === 'carousel' && (
        <TourCarousel images={step.media.images} intervalMs={step.media.intervalMs} label={step.media.label} />
      )}

      {/* Barra de progreso */}
      <div className="klonup-tour-progress-bar">
        <div
          className="klonup-tour-progress-fill"
          data-testid="progress-bar-fill"
          style={{ width: `${totalSteps > 1 ? (stepIndex / (totalSteps - 1)) * 100 : 100}%` }}
        />
      </div>

      {/* Footer */}
      <div className="klonup-tour-footer">
        {!isFirst && (
          <button type="button" className="klonup-tour-btn klonup-tour-btn-ghost" onClick={onPrev}>
            {prevLabel}
          </button>
        )}
        <div style={{ flex: 1 }} />
        {isLast ? (
          // El último paso siempre muestra "¡Listo!" (incluso si fuese advanceOn).
          <button
            type="button"
            className="klonup-tour-btn klonup-tour-btn-primary"
            onClick={onDone}
          >
            {doneLabel}
          </button>
        ) : (
          // Paso intermedio. En pasos advanceOn "Siguiente" sigue disponible: el motor lo
          // resuelve haciendo click programático en el target/selector real (que abre el panel
          // o el modo edición) antes de avanzar, igual que si el usuario lo pulsara a mano.
          <button
            type="button"
            className="klonup-tour-btn klonup-tour-btn-primary"
            onClick={onNext}
          >
            {nextLabel}
          </button>
        )}
      </div>
    </div>
  );
}
